import type { AgentExportRow, ImportDestinationBody } from "../api/agentsApi";
import { normaliseName } from "./dataSheetTransfer";

/**
 * Shared column model for the Destinations export/import. Only the fields
 * the destination detail screen edits are exported, plus the reference
 * (agentRef) and the PBX login the PBX generated for the agent.
 */

export const STATUS_OPTIONS: { id: number; label: string }[] = [
  { id: 1, label: "Unavailable" },
  { id: 2, label: "Available" },
  { id: 3, label: "Paused" },
  { id: 4, label: "Do not disturb" },
];

export const CLI_OPTIONS: { id: number; label: string }[] = [
  { id: 1, label: "Calling number" },
  { id: 2, label: "Anonymous" },
  { id: 3, label: "Called number" },
];

export type DestinationColumnKey =
  | "agentName"
  | "emailAddress"
  | "username"
  | "status"
  | "destination"
  | "dialTimeout"
  | "cliType"
  | "voicemailEnabled"
  | "voicemailSendEmail"
  | "maxChannels"
  | "recordCalls"
  | "muteWhisper"
  | "agentSignoffSMS"
  | "pbxEnabled"
  | "pbxUsername"
  | "pbxPassword"
  | "pbxExtension";

export type ColumnKind = "text" | "int" | "bool" | "status" | "cli";

export interface DestinationColumn {
  key: DestinationColumnKey;
  /** Section in the export dialog. */
  group: DestinationColumnGroup;
  /** Header text in the exported file and label in the UI. */
  label: string;
  kind: ColumnKind;
  /** false = export only (the reference is assigned by the system). */
  importable: boolean;
  /** Extra header spellings accepted when suggesting a mapping. */
  aliases: string[];
  hint?: string;
}

export type DestinationColumnGroup = "Destination" | "Call handling" | "Voicemail" | "PBX";

/** Group order in the export dialog; the columns of the file follow the same order. */
export const DESTINATION_COLUMN_GROUPS: DestinationColumnGroup[] = ["Destination", "Call handling", "Voicemail", "PBX"];

export const DESTINATION_COLUMNS: DestinationColumn[] = [
  // Who the destination is
  { key: "agentName", group: "Destination", label: "Name", kind: "text", importable: true, aliases: ["agentName", "agent", "destination name"], hint: "Required" },
  { key: "destination", group: "Destination", label: "Number", kind: "text", importable: true, aliases: ["destination", "phone", "phone number", "nummer"] },
  { key: "status", group: "Destination", label: "Status", kind: "status", importable: true, aliases: ["statusName", "statusId"], hint: STATUS_OPTIONS.map((s) => s.label).join(" / ") },
  { key: "username", group: "Destination", label: "Username", kind: "text", importable: true, aliases: ["login", "user name"] },
  { key: "emailAddress", group: "Destination", label: "Email", kind: "text", importable: true, aliases: ["emailAddress", "e-mail", "mail"] },
  { key: "agentSignoffSMS", group: "Destination", label: "Mobile (SMS)", kind: "text", importable: true, aliases: ["agentSignoffSMS", "signOffSMS", "mobile", "sms"] },
  // How calls are offered
  { key: "dialTimeout", group: "Call handling", label: "Attempt timeout (sec)", kind: "int", importable: true, aliases: ["dialTimeout", "timeout", "attempt timeout"] },
  { key: "maxChannels", group: "Call handling", label: "Max channels", kind: "int", importable: true, aliases: ["maxChannels", "channels"] },
  { key: "muteWhisper", group: "Call handling", label: "Mute whisper", kind: "bool", importable: true, aliases: ["muteWhisper"] },
  { key: "recordCalls", group: "Call handling", label: "Record calls", kind: "bool", importable: true, aliases: ["recordCalls", "recording", "recordings"] },
  // Voicemail
  { key: "voicemailEnabled", group: "Voicemail", label: "Voicemail enabled", kind: "bool", importable: true, aliases: ["voicemailEnabled", "voicemail"] },
  { key: "voicemailSendEmail", group: "Voicemail", label: "Voicemail send email", kind: "bool", importable: true, aliases: ["voicemailSendEmail", "voicemail email"] },
  // PBX login and extension
  { key: "cliType", group: "PBX", label: "Phone display (CLI)", kind: "cli", importable: true, aliases: ["cliTypeName", "cliTypeId", "cli", "cli type"], hint: CLI_OPTIONS.map((c) => c.label).join(" / ") },
  { key: "pbxEnabled", group: "PBX", label: "PBX enabled", kind: "bool", importable: true, aliases: ["pbxEnabled", "pbx", "allow login on the smx pbx"] },
  { key: "pbxUsername", group: "PBX", label: "PBX username", kind: "text", importable: true, aliases: ["pbxUsername", "pbx user"], hint: "Optional; generated when empty and PBX is enabled" },
  { key: "pbxPassword", group: "PBX", label: "PBX password", kind: "text", importable: true, aliases: ["pbxPassword", "pbx pass"], hint: "Optional; generated when empty and PBX is enabled" },
  { key: "pbxExtension", group: "PBX", label: "Extension number", kind: "text", importable: true, aliases: ["pbxExtension", "extension", "extension number", "ext", "extensie"], hint: "Optional, exactly 4 digits, unique within the customer" },
];

export const columnByKey = (key: DestinationColumnKey): DestinationColumn =>
  DESTINATION_COLUMNS.find((c) => c.key === key)!;

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

const boolText = (b: boolean) => (b ? "Yes" : "No");
const optionLabel = (options: { id: number; label: string }[], id: number, fallback: string) =>
  options.find((o) => o.id === id)?.label ?? fallback;

/** Cell text for one export column of one agent. */
export function exportValue(row: AgentExportRow, key: DestinationColumnKey): string {
  switch (key) {
    case "agentName": return row.agentName;
    case "emailAddress": return row.emailAddress;
    case "username": return row.username;
    case "status": return optionLabel(STATUS_OPTIONS, row.statusId, row.statusName);
    case "destination": return row.destination;
    case "dialTimeout": return String(row.dialTimeout);
    case "cliType": return optionLabel(CLI_OPTIONS, row.cliTypeId, row.cliTypeName);
    case "voicemailEnabled": return boolText(row.voicemailEnabled);
    case "voicemailSendEmail": return boolText(row.voicemailSendEmail);
    case "maxChannels": return String(row.maxChannels);
    case "recordCalls": return boolText(row.recordCalls);
    case "muteWhisper": return boolText(row.muteWhisper);
    case "agentSignoffSMS": return row.agentSignoffSMS;
    case "pbxEnabled": return boolText(row.pbxEnabled);
    case "pbxUsername": return row.pbxUsername;
    case "pbxPassword": return row.pbxPassword;
    case "pbxExtension": return row.pbxExtension ?? "";
  }
}

// ---------------------------------------------------------------------------
// Import: mapping + parsing
// ---------------------------------------------------------------------------

/** Proposes file column → destination field by header name (label, key or alias). */
export function suggestDestinationMapping(headers: string[]): (DestinationColumnKey | null)[] {
  const byName = new Map<string, DestinationColumnKey>();
  for (const c of DESTINATION_COLUMNS) {
    if (!c.importable) continue;
    byName.set(normaliseName(c.label), c.key);
    byName.set(normaliseName(c.key), c.key);
    for (const a of c.aliases) byName.set(normaliseName(a), c.key);
  }
  const used = new Set<DestinationColumnKey>();
  return headers.map((h) => {
    const key = byName.get(normaliseName(h));
    if (key && !used.has(key)) {
      used.add(key);
      return key;
    }
    return null;
  });
}

const TRUE_WORDS = new Set(["yes", "y", "true", "1", "ja", "j", "x", "on", "enabled"]);
const FALSE_WORDS = new Set(["no", "n", "false", "0", "nee", "off", "disabled"]);

/** "Yes"/"1"/"ja" → true, "No"/"0"/"nee" → false, "" → null, else undefined (invalid). */
export function parseBool(raw: string): boolean | null | undefined {
  const s = raw.trim().toLowerCase();
  if (!s) return null;
  if (TRUE_WORDS.has(s)) return true;
  if (FALSE_WORDS.has(s)) return false;
  return undefined;
}

/** Whole non-negative number; "" → null; invalid → undefined. */
export function parseIntCell(raw: string): number | null | undefined {
  const s = raw.trim();
  if (!s) return null;
  return /^\d+$/.test(s) ? Number(s) : undefined;
}

/**
 * Matches a UI label ("Calling number"), a database name ("Pass-thru", from
 * the lookups) or an id ("1"); "" → null; unknown → undefined. Only ids
 * that exist in the UI option list are accepted.
 */
export function parseOption(
  raw: string,
  options: { id: number; label: string }[],
  dbNames: { id: number; name: string }[] = [],
): number | null | undefined {
  const s = raw.trim();
  if (!s) return null;
  const known = (id: number) => options.some((o) => o.id === id);
  if (/^\d+$/.test(s)) return known(Number(s)) ? Number(s) : undefined;
  const n = normaliseName(s);
  const byLabel = options.find((o) => normaliseName(o.label) === n);
  if (byLabel) return byLabel.id;
  const byDbName = dbNames.find((d) => normaliseName(d.name) === n && known(d.id));
  return byDbName ? byDbName.id : undefined;
}

/** Database names used as extra spellings for Status / Phone display (CLI). */
export interface OptionNames {
  statuses: { id: number; name: string }[];
  cliTypes: { id: number; name: string }[];
}

export interface ParsedDestinationRow {
  /** Body for the import call (without replaceAgentId); null when the row has errors. */
  body: Omit<ImportDestinationBody, "replaceAgentId"> | null;
  /** Human-readable problems; the import is blocked while any row has one. */
  errors: string[];
}

/**
 * Turns the mapped cells of one file row into an import body. Only mapped,
 * non-empty cells are sent; everything else stays at the defaults a new
 * destination gets.
 */
export function parseDestinationRow(
  cells: Partial<Record<DestinationColumnKey, string>>,
  names: OptionNames = { statuses: [], cliTypes: [] },
): ParsedDestinationRow {
  const errors: string[] = [];
  const text = (k: DestinationColumnKey): string | null => {
    const v = cells[k];
    return v == null || v.trim() === "" ? null : v.trim();
  };
  const bool = (k: DestinationColumnKey): boolean | null => {
    const v = cells[k];
    if (v == null) return null;
    const b = parseBool(v);
    if (b === undefined) errors.push(`${columnByKey(k).label}: "${v}" is not Yes/No`);
    return b ?? null;
  };
  const int = (k: DestinationColumnKey): number | null => {
    const v = cells[k];
    if (v == null) return null;
    const n = parseIntCell(v);
    if (n === undefined) errors.push(`${columnByKey(k).label}: "${v}" is not a whole number`);
    return n ?? null;
  };

  const agentName = text("agentName");
  if (!agentName) errors.push("Name is required");
  const pbxExtension = text("pbxExtension");
  if (pbxExtension && !/^[0-9]{4}$/.test(pbxExtension)) errors.push(`Extension number: "${pbxExtension}" is not exactly 4 digits`);

  const statusRaw = cells.status;
  const statusId = statusRaw == null ? null : parseOption(statusRaw, STATUS_OPTIONS, names.statuses);
  if (statusId === undefined) errors.push(`Status: "${statusRaw}" is unknown`);
  const cliRaw = cells.cliType;
  const cliTypeId = cliRaw == null ? null : parseOption(cliRaw, CLI_OPTIONS, names.cliTypes);
  if (cliTypeId === undefined) errors.push(`Phone display (CLI): "${cliRaw}" is unknown`);

  const pbxUsername = text("pbxUsername");
  if (pbxUsername && /\s/.test(pbxUsername)) errors.push("PBX username may not contain spaces");

  const body: Omit<ImportDestinationBody, "replaceAgentId"> = {
    agentName: agentName ?? "",
    emailAddress: text("emailAddress"),
    username: text("username"),
    statusId: statusId ?? null,
    destination: text("destination"),
    dialTimeout: int("dialTimeout"),
    cliTypeId: cliTypeId ?? null,
    voicemailEnabled: bool("voicemailEnabled"),
    voicemailSendEmail: bool("voicemailSendEmail"),
    maxChannels: int("maxChannels"),
    recordCalls: bool("recordCalls"),
    muteWhisper: bool("muteWhisper"),
    agentSignoffSMS: text("agentSignoffSMS"),
    pbxEnabled: bool("pbxEnabled"),
    pbxUsername,
    pbxPassword: text("pbxPassword"),
    pbxExtension,
  };
  return { body: errors.length ? null : body, errors };
}
